import { useEffect, useRef, useState } from "react";
import { isValidPin, keypairFromSecret, publicKeyToNpub, wrapKey } from "@timecapsule/crypto";
import { api, type ServerCopy } from "../api";
import { isLocked, openServerCopy } from "../keys";
import { isAborted, passkeyPrf } from "../passkey";
import { navigate } from "../router";
import { useKeyOwner } from "../members";
import { useSession } from "../session";
import { errorText, useT } from "../i18n";
import { Loading } from "../Loading";
import { PhraseInput, ProtectKey } from "./KeySetup";
import { useRefreshHandler } from "../Refresh";

/**
 * Sets up the key on a device that does not have it (PLAN.md 4.6, "new device"):
 *   1. synced passkey + PIN open the server copy (one counted PIN attempt), or
 *   2. the recovery phrase rebuilds the key; then a new PIN and passkey protect it and
 *      replace the server copy (this also unblocks a blocked server copy).
 * After a Google re-link the phrase key arrives as `pendingKey` and goes straight to step 2.
 */
export function Restore() {
  const t = useT();
  const { me, pendingKey, setPendingKey } = useSession();
  const [server, setServer] = useState<ServerCopy | null | undefined>(undefined);
  const [mode, setMode] = useState<"passkey" | "phrase">("passkey");
  const [error, setError] = useState<string | null>(null);
  useRefreshHandler(async () => { setError(null); setServer(await api.loadServerCopy()); });

  useEffect(() => {
    api.loadServerCopy().then(setServer, (err: unknown) => setError(errorText(err)));
  }, []);

  const handedOver = pendingKey && keypairFromSecret(pendingKey).publicKey === me.pubkey ? pendingKey : null;
  if (handedOver) {
    return (
      <div className="narrow">
        <h1>{t.restore.welcomeBack(me.display_name)}</h1>
        <p>{t.restore.relinked(publicKeyToNpub(me.pubkey!))}</p>
        <ProtectKey
          secretKey={handedOver}
          serverCopy
          onDone={() => {
            setPendingKey(null);
            navigate("/inbox");
          }}
        />
      </div>
    );
  }

  if (server === undefined && !error) return <Loading />;
  const usable = !!server && !server.locked;

  return (
    <div className="narrow">
      <h1>{t.restore.title}</h1>
      {me.pubkey && <p className="small muted">{t.restore.identity(publicKeyToNpub(me.pubkey))}</p>}
      {server?.locked && <p className="warn">{t.restore.blocked}</p>}
      {usable && mode === "passkey" ? (
        <>
          <FromServerCopy server={server} />
          <p className="small">
            {t.restore.noPasskey}{" "}
            <button className="link" onClick={() => setMode("phrase")}>
              {t.restore.usePhrase}
            </button>
          </p>
        </>
      ) : (
        <>
          <FromPhrase />
          {usable && (
            <p className="small">
              <button className="link" onClick={() => setMode("passkey")}>
                {t.restore.usePasskeyInstead}
              </button>
            </p>
          )}
        </>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function FromServerCopy({ server }: { server: ServerCopy }) {
  const t = useT();
  const { adoptKey } = useSession();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  async function open() {
    setError(null);
    setBusy(true);
    try {
      // Passkey first: a cancelled prompt must not cost one of the server's PIN attempts.
      abort.current = new AbortController();
      setWaiting(true);
      const prf = await passkeyPrf(server.copy.credentialId, abort.current.signal);
      setWaiting(false);
      const key = await openServerCopy(server.copy, pin, prf);
      // Same passkey and PIN lock the device copy; the server copy stays as it is.
      const copy = await wrapKey(key, "prf-pin", { prf, pin }, server.copy.credentialId);
      await adoptKey({ kind: "prf-pin", pubkey: copy.pubkey, copy }, key);
      navigate("/inbox");
    } catch (err) {
      setWaiting(false);
      if (!isAborted(err)) setError(isLocked(err) ? t.restore.blockedShort : errorText(err));
      setBusy(false);
    }
  }

  return (
    <>
      <p>{t.restore.passkeyText}</p>
      <div className="fields">
        <label>
          {t.common.pin}
          <input type="password" inputMode="numeric" autoComplete="current-password" value={pin} disabled={busy} onChange={(e) => setPin(e.target.value.trim())} />
        </label>
      </div>
      <p className="small muted">{t.restore.attemptsLeft(server.attempts_left)}</p>
      <button className="primary" disabled={!isValidPin(pin) || busy} onClick={open}>
        {waiting ? t.common.waitingPasskey : busy ? t.common.opening : t.restore.usePasskey}
      </button>
      {waiting && (
        <button type="button" onClick={() => abort.current?.abort()}>
          {t.common.cancel}
        </button>
      )}
      {waiting && <p className="small muted">{t.common.noWindowHint}</p>}
      {error && <p className="error">{error}</p>}
    </>
  );
}

function FromPhrase() {
  const t = useT();
  const { me } = useSession();
  const [secretKey, setSecretKey] = useState<Uint8Array | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const pubkey = secretKey ? keypairFromSecret(secretKey).publicKey : null;
  const other = !!pubkey && !!me.pubkey && pubkey !== me.pubkey;
  const ownedBy = useKeyOwner(pubkey);

  if (secretKey && confirmed) return <ProtectKey secretKey={secretKey} serverCopy onDone={() => navigate("/inbox")} />;

  return (
    <>
      <p>{t.restore.phraseText}</p>
      <PhraseInput onKey={setSecretKey} />
      {ownedBy ? <p className="error">{t.restore.otherMember(ownedBy.display_name)}</p> : other && <p className="warn">{t.restore.otherIdentity(publicKeyToNpub(pubkey!))}</p>}
      <button className="primary" disabled={!secretKey || !!ownedBy} onClick={() => setConfirmed(true)}>
        {other ? t.restore.useThis : t.common.continue}
      </button>
    </>
  );
}
